<template>
  <br />
  <div style="padding:5px" class="row">
    <h4 class="text-center">Purchased gifts of user #{{ user_id }}</h4>
    <div class="col-lg-4 col-md-6 col-sm-12 mb-3">
      <label for="per-page" class="form-label me-1 mt-2">Per page</label>
      <select id="per-page" class="form-control" v-model="perPage" @change="perPageFilter(perPage)">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
      </select>
    </div>

    <div class="alert alert-success" role="alert" v-if="success">
      {{ success }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="error">
      {{ error }}
    </div>

    <router-link :to="{ name: 'SingleUser', params: { id: user_id } }">Back to user</router-link>

    <table class="table">
      <thead>
      <tr>
        <th scope="col"># ID</th>
        <th scope="col">Gift Type</th>
        <th scope="col">Status</th>
        <th scope="col">Buyer</th>
        <th scope="col">Gift item</th>
        <th scope="col">Redeemer</th>
        <th scope="col">Dates</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="loading">
        <td colspan="7" class="text-center"><font-awesome-icon icon="fa-solid fa-sync fa-10x" spin /></td>
      </tr>
      <tr v-else-if="gifts.length === 0">
        <td colspan="7" class="text-center">No gifts found</td>
      </tr>
      <template v-else>
        <tr v-for="gift in gifts" :key="gift.id">
          <th scope="row">{{ gift.id }}</th>
          <td>
            <span class="badge badge-info text-capitalize">{{ gift.type }}</span>
          </td>
          <td>
            <span v-if="gift.status === 'redeemed'" class="badge badge-success text-capitalize">{{ gift.status}}</span>
            <span v-else class="badge badge-secondary text-capitalize">{{gift.status}}</span>
          </td>
          <td>
            <div v-if="gift.gift_payment">
              {{ gift.gift_payment.user.name }} <br/>
              {{ gift.gift_payment.user.email }}
            </div>
          </td>

          <td>
            <div v-if="gift.type === 'subscription'">{{ gift.tier.name }} ({{ gift.gift_payment.channel.name }})</div>
            <div v-else-if="gift.type === 'post'">{{ gift.post?.title ?? 'Unknown' }} - ({{ gift.gift_payment.channel.name }})</div>
            <div v-else-if="gift.type === 'category'">{{ gift.category.name }} - ({{ gift.gift_payment.channel.name }})</div>

            <a target="_blank" :href="gift.redeem_link">Redeem Link</a>

          </td>
          <td>
            <div v-if="gift.redeem">{{ gift.redeem.user.name }} <br/> {{gift.redeem.user.email}}</div>
            <div v-else>-</div>
          </td>
          <td>
            <font-awesome-icon icon="fa-solid fa-calendar-plus"></font-awesome-icon>
            Created at: {{ gift.created_at }} <br/>
            <span v-if="gift.redeem">
                <font-awesome-icon icon="fa-solid fa-check"></font-awesome-icon> Redeemed at: {{ gift.redeem.redeemed_at }}
              </span>
            <span v-else>
                <font-awesome-icon icon="fa-solid fa-clock"></font-awesome-icon> Expires at: {{ gift.expires_at ? gift.expires_at : '-' }}
              </span>
          </td>
        </tr>
      </template>
      </tbody>
    </table>
    <div class="d-flex justify-content-between" v-if="totalRecords >= 1">
      <div>
        <div class="p-2">
          <p>Total gifts: {{ totalRecords }}</p>
        </div>
      </div>
      <div>
        <div class="p-2">
          <nav v-if="totalPages >= 1" aria-label="Page navigation example">
            <ul class="pagination">
              <li class="page-item" :class="{ disabled: currentPage - 1 < 1 }">
                <a @click="paginate(currentPage - 1)" class="page-link">Previous</a>
              </li>
              <li class="page-item" :class="{ active: 1 === currentPage }">
                <a @click="paginate(1)" class="page-link" :class="{ 'not-clickable': currentPage === 1 }">1</a>
              </li>
              <li class="page-item" v-if="currentPage - 2 > 1">
                <a href="#" class="page-link">...</a>
              </li>
              <li class="page-item" v-if="currentPage - 2 > 1">
                <a @click="paginate(currentPage - 2)" class="page-link">{{ currentPage - 2 }}</a>
              </li>
              <li class="page-item" v-if="currentPage - 1 > 1">
                <a @click="paginate(currentPage - 1)" class="page-link">{{ currentPage - 1 }}</a>
              </li>
              <li class="page-item" v-if="currentPage !== 1" :class="{ active: currentPage }">
                <a @click="paginate(currentPage)" class="page-link" :class="{ 'not-clickable': currentPage }">{{
                    currentPage }}</a>
              </li>
              <li class="page-item" v-if="currentPage + 1 <= totalPages">
                <a @click="paginate(currentPage + 1)" class="page-link">{{ currentPage + 1 }}</a>
              </li>
              <li class="page-item" v-if="currentPage + 2 <= totalPages">
                <a @click="paginate(currentPage + 2)" class="page-link">{{ currentPage + 2 }}</a>
              </li>
              <li class="page-item" v-if="currentPage + 2 < totalPages">
                <a href="#" class="page-link">...</a>
              </li>
              <li class="page-item" v-if="currentPage !== totalPages && currentPage + 2 < totalPages"
                  :class="{ active: totalPages === currentPage }">
                <a @click="paginate(totalPages)" class="page-link"
                   :class="{ 'not-clickable': totalPages === currentPage }">{{ totalPages }}</a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage + 1 > totalPages }">
                <a @click="paginate(currentPage + 1)" class="page-link">Next</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>


<script>

import axios from 'axios';
import _debounce from 'lodash/debounce';
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
  components: {FontAwesomeIcon},
  data() {
    return {
      loading: true,
      gifts: [],
      user_id: this.$route.params.id,
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      perPage: 10,
      success: '',
      error: ''
    }
  },

  mounted() {
    this.getUserGifts(this.user_id, 1, this.perPage);
  },

  methods: {
    debouncedGetGifts: _debounce(function (user_id, page, perPage) {
      this.loading = true;
      axios.get(`/admin/gifts/get?page=${page}&per_page=${perPage}&user_id=${user_id}`).then(response => {
        this.gifts = response.data.data;
        this.totalPages = response.data.totalPages;
        this.totalRecords = response.data.totalRecords;
        this.loading = false;
      })
          .catch(() => {
            this.loading = false;
          });
    }, 500),
    getUserGifts(user_id, page, perPage) {
      this.loading = true;
      this.debouncedGetGifts(user_id, page, perPage);
    },
    perPageFilter(perPage) {
      this.perPage = perPage;
      this.currentPage = 1;
      this.getUserGifts(this.user_id, this.currentPage, this.perPage);
    },
    paginate(page) {

      this.currentPage = page;
      this.getUserGifts(this.user_id, this.currentPage, this.perPage);
    },
  }
}
</script>
